import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const values = [
    {
      icon: 'ri-map-pin-line',
      title: 'Local Pride',
      description:
        'Born and raised in Starkville. Every shirt is designed, printed, and shipped right here in the heart of Bulldogs country.',
    },
    {
      icon: 'ri-printer-line',
      title: 'Quality Prints',
      description:
        'Premium DTG printing on soft, durable fabrics. Your designs stay vibrant wash after wash, game after game.',
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Turnaround',
      // Escape the apostrophe inside the string to avoid a syntax error
      description:
        "Most custom orders ship within 48 hours. Need it for game day? We\'ve got you covered with rush options.",
    },
    {
      icon: 'ri-heart-line',
      title: 'Community',
      description:
        "We're Bulldogs fans serving Bulldogs fans. Supporting local businesses, students, and the Starkville community we love.",
    },
  ];

  const stats = [
    { value: '2,400+', label: 'Shirts Printed' },
    { value: '4.9★', label: 'Average Rating' },
    { value: '48hr', label: 'Avg Turnaround' },
    { value: '100%', label: 'Local Made' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/82ad8c75777da265d85bbd063cac2e03/47c2635391ea4157c1affef39fe9b4f6.jpeg"
            alt="Starkville Mississippi"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <span className="inline-block px-5 py-2 bg-white/20 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-widest rounded-full mb-6 border border-white/30">
            Our Story
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
            Made in Starkville,<br />For Bulldogs Fans
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-medium max-w-2xl mx-auto leading-relaxed">
            Local custom apparel shop bringing your Mississippi State spirit to life, one shirt at a time.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-red-50 text-[#6B0000] text-xs font-bold uppercase tracking-widest rounded-full mb-6">
                Since 2020
              </span>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                Built by Bulldogs,<br />For Bulldogs
              </h2>
              <div className="space-y-5 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  <strong className="text-gray-900">On Target Prints</strong> started in a
                  small Starkville studio with one mission: create custom Bulldogs gear that fans
                  actually want to wear.
                </p>
                <p className="text-lg">
                  We're not a faceless online store. We're your neighbors, fellow fans, and proud
                  members of the Starkville community. Every shirt is designed and printed right
                  here in town—no outsourcing, no shortcuts.
                </p>
                <p className="text-lg">
                  Whether you're tailgating at The Junction, cheering in Davis Wade, or repping
                  maroon and white across the country, we're here to help you show your Bulldogs
                  pride in style.
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/customize"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#6B0000] text-white font-bold rounded-lg hover:bg-[#5A0000] transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Design Your Shirt
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/dce1cfbc8a357f3f54c474a6a847110c.jpg"
                  alt="On Target Prints Studio"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 border-4 border-[#6B0000]">
                <div className="text-4xl font-black text-[#6B0000]">2,400+</div>
                <div className="text-sm font-bold text-gray-600 uppercase tracking-wide">
                  Shirts Printed
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-red-50 text-[#6B0000] text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              What We Stand For
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at On Target Prints.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#6B0000] rounded-xl mb-6">
                  <i className={`${value.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-20 px-6 bg-[#6B0000]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-black text-white mb-2">{stat.value}</div>
                <div className="text-sm font-bold text-white/80 uppercase tracking-widest">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-red-50 text-[#6B0000] text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              Get In Touch
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Visit Us in Starkville
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Stop by our studio, send us a message, or connect on social media.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#6B0000] rounded-full mx-auto mb-6">
                <i className="ri-map-pin-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-3">Location</h3>
              <p className="text-gray-600 leading-relaxed">
                1085 Stark Road Suite 106<br />
                Starkville, MS 39759<br />
                <span className="text-sm text-gray-500">Heart of Bulldogs Country</span>
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#6B0000] rounded-full mx-auto mb-6">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-3">Phone</h3>
              <a href="tel:6622424286" className="text-[#6B0000] hover:underline font-medium">
                (662) 242-4286
              </a>
              <p className="text-sm text-gray-500 mt-2">Ask for Lisa</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#6B0000] rounded-full mx-auto mb-6">
                <i className="ri-mail-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-3">Email</h3>
              <a href="mailto:info@ontargetprintsolutions.com" className="text-[#6B0000] hover:underline font-medium text-sm">
                info@ontargetprintsolutions.com
              </a>
              <p className="text-sm text-gray-500 mt-2">We reply within 24 hours</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="flex justify-center gap-4 mb-6">
              {[
                { icon: 'ri-facebook-fill', href: '#' },
                { icon: 'ri-instagram-line', href: '#' },
                { icon: 'ri-twitter-x-line', href: '#' },
                { icon: 'ri-tiktok-line', href: '#' },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  rel="nofollow"
                  className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-[#6B0000] hover:text-white transition-all cursor-pointer"
                >
                  <i className={`${social.icon} text-xl`}></i>
                </a>
              ))}
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Send Us a Message
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#6B0000] via-[#8B0000] to-[#6B0000] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
            Ready to Design Your<br />Perfect Bulldogs Shirt?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Use our custom design studio to create exactly what you want. Printed locally in
            Starkville and shipped fast.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/customize"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white text-[#6B0000] font-black rounded-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap text-lg"
            >
              Start Designing
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white/10 backdrop-blur-sm text-white font-bold rounded-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap text-lg"
            >
              Shop Bulldogs Tees
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
